import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CONTACT_INFO } from '../data/mock';
import { CalendlyEmbed } from '../components/CalendlyEmbed';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import * as Icons from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function BookPage() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Please fill in your name and email.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/booking-message`, form);
      toast.success('Message sent! We\'ll get back to you shortly.');
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <section className="pt-32 pb-10 px-6" style={{ background: 'radial-gradient(at 53% 78%, hsla(60,100%,50%,0.12) 0px, transparent 50%), radial-gradient(at 71% 91%, hsla(108,100%,50%,0.12) 0px, transparent 50%)' }}>
        <div className="max-w-[800px] mx-auto text-center">
          <motion.div {...fadeInUp}>
            <Badge className="mb-5 bg-[rgba(148,242,127,0.12)] text-[rgb(13,121,22)] border-[rgba(148,242,127,0.3)] px-4 py-1.5 text-xs font-medium rounded-full">Free Consultation</Badge>
          </motion.div>
          <motion.h1 {...fadeInUp} transition={{ delay: 0.1 }}
            className="font-bold text-[rgb(0,55,32)] tracking-tight mb-3"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', lineHeight: '0.92' }}>
            Book Your Free Consultation
          </motion.h1>
          <motion.p {...fadeInUp} transition={{ delay: 0.2 }}
            className="text-[rgb(131,146,140)] max-w-[500px] mx-auto" style={{ fontSize: 'clamp(1rem, 2vw, 1.15rem)' }}>
            Choose a time that works best for you. No obligation.
          </motion.p>
        </div>
      </section>

      {/* Calendly Embed */}
      <section className="py-8 bg-white">
        <div className="max-w-[1000px] mx-auto px-6">
          <motion.div {...fadeInUp} className="rounded-2xl border border-black/10 overflow-hidden shadow-sm">
            <CalendlyEmbed />
          </motion.div>
        </div>
      </section>

      {/* Fallback Contact */}
      <section className="py-16" style={{ background: 'rgba(0,0,0,0.02)' }}>
        <div className="max-w-[600px] mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-8">
            <h2 className="font-semibold text-[rgb(0,55,32)] text-xl mb-2">Prefer to Reach Out Directly?</h2>
            <p className="text-sm text-[rgb(131,146,140)]">Call us or send a quick message below.</p>
            <a href={`tel:${CONTACT_INFO.phone}`} className="inline-flex items-center gap-2 mt-3 text-base font-semibold text-[rgb(13,121,22)] no-underline">
              <Icons.Phone className="w-5 h-5" /> {CONTACT_INFO.phone}
            </a>
          </motion.div>
          <motion.form {...fadeInUp} onSubmit={handleSubmit} className="space-y-4 bg-white border border-black/10 rounded-xl p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[rgb(0,55,32)]">Name *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Your name" className="rounded-lg h-11" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[rgb(0,55,32)]">Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="you@example.com" className="rounded-lg h-11" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[rgb(0,55,32)]">Message</Label>
              <Textarea value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} placeholder="How can we help?" className="rounded-lg" />
            </div>
            <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] text-white font-semibold text-sm hover:shadow-md transition-shadow disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ? 'Sending...' : 'Send Message'} {!loading && <Icons.Send className="w-4 h-4" />}
            </button>
          </motion.form>
        </div>
      </section>
    </main>
  );
}
